import React from 'react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useDroppable } from '@dnd-kit/core';
import {
  Layout,
  Image as ImageIcon,
  Type,
  List,
  MousePointerClick,
  Link2,
  Minus,
  Space,
  FileCode,
  GripVertical,
  Trash2,
  Music
} from 'lucide-react';
import './TemplateEditor.css';

// Sortable Block Item Component
const SortableBlockItem = ({ block, isSelected, onSelect, onDelete }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: block.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const getBlockLabel = (type) => {
    const labels = {
      header: 'Header',
      hero: 'Hero',
      content: 'Content',
      tracklist: 'Tracklist',
      'music-player': 'Music Player',
      cta: 'CTA Buttons',
      social: 'Social Links',
      footer: 'Footer',
      custom: 'Custom HTML',
      spacer: 'Spacer',
      divider: 'Divider',
    };
    return labels[type] || type;
  };

  const getBlockIcon = (type) => {
    const iconProps = { size: 18, strokeWidth: 2 };
    const icons = {
      header: <Layout {...iconProps} />,
      hero: <ImageIcon {...iconProps} />,
      content: <Type {...iconProps} />,
      tracklist: <List {...iconProps} />,
      'music-player': <Music {...iconProps} />,
      cta: <MousePointerClick {...iconProps} />,
      social: <Link2 {...iconProps} />,
      footer: <Layout {...iconProps} />,
      custom: <FileCode {...iconProps} />,
      spacer: <Space {...iconProps} />,
      divider: <Minus {...iconProps} />,
    };
    return icons[type] || <FileCode {...iconProps} />;
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`block-item ${isSelected ? 'selected' : ''}`}
      onClick={() => onSelect(block.id)}
    >
      <div className="block-item-drag" {...attributes} {...listeners}>
        <GripVertical size={16} strokeWidth={2} className="drag-handle" />
      </div>

      <div className="block-item-content">
        <span className="block-item-icon">{getBlockIcon(block.type)}</span>
        <div className="block-item-info">
          <div className="block-item-label">{getBlockLabel(block.type)}</div>
          <div className="block-item-type">{block.type}</div>
        </div>
      </div>

      <button
        className="block-item-delete"
        onClick={(e) => {
          e.stopPropagation();
          if (window.confirm(`Delete ${getBlockLabel(block.type)} block?`)) {
            onDelete(block.id);
          }
        }}
      >
        <Trash2 size={16} strokeWidth={2} />
      </button>
    </div>
  );
};

// Droppable zone for adding new blocks
const DroppableZone = ({ children }) => {
  const { setNodeRef, isOver } = useDroppable({
    id: 'template-drop-zone',
  });

  return (
    <div
      ref={setNodeRef}
      className={`template-drop-zone ${isOver ? 'is-over' : ''}`}
    >
      {children}
    </div>
  );
};

// Main Template Editor Component
const TemplateEditor = ({
  blocks,
  selectedBlockId,
  onSelectBlock,
  onUpdateBlock,
  onDeleteBlock,
  onReorderBlocks,
  onAddBlock,
}) => {
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragEnd = (event) => {
    const { active, over } = event;

    if (!over) return;

    // Check if dragging from palette (adding new block)
    if (active.data.current?.source === 'palette') {
      const blockType = active.data.current.type;
      onAddBlock(blockType);
      return;
    }

    // Otherwise, reordering existing blocks
    if (active.id !== over.id) {
      const oldIndex = blocks.findIndex((block) => block.id === active.id);
      const newIndex = blocks.findIndex((block) => block.id === over.id);

      const reorderedBlocks = arrayMove(blocks, oldIndex, newIndex).map(
        (block, index) => ({
          ...block,
          position: index * 10,
        })
      );

      onReorderBlocks(reorderedBlocks);
    }
  };

  return (
    <div className="template-editor">
      <div className="template-editor-header">
        <h3>Template Structure</h3>
        <p className="template-editor-subtitle">
          {blocks.length} {blocks.length === 1 ? 'block' : 'blocks'}
        </p>
      </div>

      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
      >
        <DroppableZone>
          {blocks.length === 0 ? (
            <div className="template-editor-empty">
              <p>Drag blocks here to get started</p>
              <p className="template-editor-empty-hint">
                Or click blocks from the palette above
              </p>
            </div>
          ) : (
            <SortableContext
              items={blocks.map((b) => b.id)}
              strategy={verticalListSortingStrategy}
            >
              <div className="template-editor-list">
                {blocks.map((block) => (
                  <SortableBlockItem
                    key={block.id}
                    block={block}
                    isSelected={block.id === selectedBlockId}
                    onSelect={onSelectBlock}
                    onDelete={onDeleteBlock}
                  />
                ))}
              </div>
            </SortableContext>
          )}
        </DroppableZone>
      </DndContext>
    </div>
  );
};

export default TemplateEditor;
